// DLT Commercial Logic Constants - Final System Update

// Zone-based Line Haul rates (Updated)
export const ZONE_RATES = {
  A: { max_miles: 25, rate: 270, label: 'Zone A (0-25 miles)' },
  B: { max_miles: 40, rate: 330, label: 'Zone B (25-40 miles)' },
  C: { max_miles: Infinity, rate: null, label: 'Zone C (Over 40 miles)' }
};

// Mandatory charges (applied to EVERY container)
export const MANDATORY_CHARGES = {
  chassis_split: {
    code: 'CHASSIS_SPLIT_MANDATORY',
    label: 'Chassis Split',
    rate: 85,
    unit: 'per_container',
    mandatory: true,
    description: 'Mandatory chassis split charge'
  },
  chassis_minimum: {
    code: 'CHASSIS_MIN_MANDATORY',
    label: 'Chassis Minimum (2 days)',
    rate: 90,  // $45/day x 2 days
    unit: 'per_container',
    mandatory: true,
    description: 'Mandatory 2-day chassis minimum'
  }
};

// Accessorial Charges with quantity selectors
export const ACCESSORIAL_CHARGES = {
  pre_pull: {
    code: 'PRE_PULL',
    label: 'Pre-Pull',
    rate: 115,
    unit: 'each',
    hasQuantity: true,
    description: 'Container retrieval before delivery'
  },
  chassis_rental: {
    code: 'CHASSIS_RENTAL',
    label: 'Chassis Rental',
    rate: 45,
    unit: 'per_day',
    hasQuantity: true,
    description: 'Additional chassis rental beyond minimum'
  },
  stop_off: {
    code: 'STOP_OFF',
    label: 'Stop-Off',
    rate: 150,
    unit: 'each',
    hasQuantity: true,
    description: 'Additional stop during delivery'
  },
  detention: {
    code: 'DETENTION',
    label: 'Detention',
    rate: 85,
    unit: 'per_hour',
    hasQuantity: true,
    freeHours: 2,
    description: '$85/hour after 2 hours free'
  },
  overweight: {
    code: 'OVERWEIGHT',
    label: 'Overweight',
    rate: 175,
    unit: 'per_container',
    threshold: 44000,
    autoApply: true,
    description: 'Containers ≥ 44,000 lbs'
  },
  dry_run: {
    code: 'DRY_RUN',
    label: 'Dry Run',
    rate: 'LH+FSC',
    unit: 'calculated',
    hasQuantity: false,
    description: 'Line Haul + Fuel Surcharge'
  },
  drop_charge: {
    code: 'DROP_CHARGE',
    label: 'Drop Charge',
    rate: 50,
    unit: 'each',
    autoApply: true,
    description: 'Container drop charge'
  },
  tonu: {
    code: 'TONU',
    label: 'TONU',
    rate: 150,
    unit: 'each',
    hasQuantity: true,
    description: 'Truck Ordered Not Used'
  },
  storage: {
    code: 'STORAGE',
    label: 'Storage',
    rate: 40,
    unit: 'per_day',
    hasQuantity: true,
    description: 'Container storage per day'
  },
  hazmat: {
    code: 'HAZMAT',
    label: 'Hazmat',
    rate: 175,
    unit: 'per_container',
    hasQuantity: false,
    description: 'Hazardous materials handling'
  },
  triaxle_rental: {
    code: 'TRIAXLE',
    label: 'Triaxle Rental',
    rate: 100,
    unit: 'per_day',
    hasQuantity: true,
    description: 'Tri-axle chassis rental'
  }
};

// Container sizes (rail equipment)
export const CONTAINER_SIZES = [
  "20' Dry",
  "40' Dry",
  "40' High Cube",
  "45' High Cube",
  "53' Domestic",
  "20' Reefer",
  "40' Reefer",
  "Open Top",
  "Flat Rack",
  "Tank Container"
];

// Weight threshold for overweight fee
export const OVERWEIGHT_THRESHOLD = 44000;

// Maximum containers per submission
export const MAX_CONTAINERS = 30;

// Appointment types
export const APPOINTMENT_TYPES = [
  { value: 'Live Load', label: 'Live Load' },
  { value: 'Live Unload', label: 'Live Unload' },
  { value: 'Drop & Pick', label: 'Drop & Pick' }
];

// Rail Ramps
export const RAIL_RAMPS = [
  { value: 'NS', label: 'NS (Norfolk Southern)' },
  { value: 'CSX', label: 'CSX' }
];

// Load statuses
export const LOAD_STATUSES = [
  'Submitted',
  'Approved',
  'Picked Up',
  'Delivered',
  'Returned',
  'Completed'
];

// Validate container number (ISO format: XXXX1234567)
export const validateContainerNumber = (number) => {
  if (!number) return { valid: false, message: 'Container number is required' };
  const cleanNumber = number.toUpperCase().replace(/\s/g, '');
  const isoPattern = /^[A-Z]{4}\d{7}$/;
  if (!isoPattern.test(cleanNumber)) {
    return { 
      valid: false, 
      message: 'Invalid format. Container numbers must be 4 letters followed by 7 digits (e.g., MSCU1234567)' 
    };
  }
  return { valid: true, message: '' };
};

// Calculate per-container charges
export const calculateContainerCharges = (container, lineHaul, zone, appointmentType, dropDate, pickupDate) => {
  const charges = {
    lineHaul: zone !== 'C' ? (lineHaul || 0) : 0,
    chassisMinimum: MANDATORY_CHARGES.chassis_minimum.rate,
    chassisSplit: MANDATORY_CHARGES.chassis_split.rate,
    overweight: 0,
    hazmat: 0,
    dropCharge: 0,
    accessories: 0,
    total: 0
  };

  // Line haul (Zone A/B only)
  charges.total += charges.lineHaul;
  
  // Mandatory charges (applied to EVERY container)
  charges.total += charges.chassisMinimum;
  charges.total += charges.chassisSplit;

  // Overweight check (≥ 44,000 lbs)
  const weight = parseInt(container.weight_lbs) || 0;
  if (weight >= OVERWEIGHT_THRESHOLD) {
    charges.overweight = ACCESSORIAL_CHARGES.overweight.rate;
    charges.total += charges.overweight;
  }

  // Hazmat check
  if (container.hazmat) {
    charges.hazmat = ACCESSORIAL_CHARGES.hazmat.rate;
    charges.total += charges.hazmat;
  }

  // Drop charge check (auto-apply for Drop & Pick or different dates)
  if (appointmentType === 'Drop & Pick' || 
      (dropDate && pickupDate && dropDate !== pickupDate)) {
    charges.dropCharge = ACCESSORIAL_CHARGES.drop_charge.rate;
    charges.total += charges.dropCharge;
  }

  // Additional accessories
  if (container.accessories) {
    Object.entries(container.accessories).forEach(([key, qty]) => {
      if (qty > 0 && ACCESSORIAL_CHARGES[key]) {
        const acc = ACCESSORIAL_CHARGES[key];
        if (acc.rate !== 'LH+FSC') {
          charges.accessories += acc.rate * qty;
        }
      }
    });
    charges.total += charges.accessories;
  }

  return charges;
};

// Calculate total for all containers with full breakdown
export const calculateTotalCharges = ({ containers, lineHaul, zone, appointmentType, dropDate, pickupDate }) => {
  const numContainers = containers.length;
  
  // Initialize breakdown
  let breakdown = {
    containers: [],
    // Per-category subtotals
    lineHaul: {
      perContainer: zone !== 'C' ? (lineHaul || 0) : 0,
      count: zone !== 'C' ? numContainers : 0,
      amount: zone !== 'C' ? (lineHaul || 0) * numContainers : 0
    },
    chassisMinimum: {
      perContainer: MANDATORY_CHARGES.chassis_minimum.rate,
      count: numContainers,
      amount: MANDATORY_CHARGES.chassis_minimum.rate * numContainers
    },
    chassisSplit: {
      perContainer: MANDATORY_CHARGES.chassis_split.rate,
      count: numContainers,
      amount: MANDATORY_CHARGES.chassis_split.rate * numContainers
    },
    overweight: {
      perContainer: ACCESSORIAL_CHARGES.overweight.rate,
      count: 0,
      amount: 0
    },
    hazmat: {
      perContainer: ACCESSORIAL_CHARGES.hazmat.rate,
      count: 0,
      amount: 0
    },
    dropCharge: {
      perContainer: ACCESSORIAL_CHARGES.drop_charge.rate,
      count: 0,
      amount: 0
    },
    chassis: {
      perContainer: MANDATORY_CHARGES.chassis_minimum.rate,
      count: numContainers,
      amount: MANDATORY_CHARGES.chassis_minimum.rate * numContainers
    },
    additional: [],
    subtotalAccessorials: 0,
    total: 0,
    grossTotal: 0,
    zone,
    zoneLabel: ZONE_RATES[zone]?.label || 'Unknown Zone'
  };

  // Check if drop charge should apply
  const shouldApplyDropCharge = appointmentType === 'Drop & Pick' || 
    (dropDate && pickupDate && dropDate !== pickupDate);

  containers.forEach((container, index) => {
    const charges = calculateContainerCharges(container, lineHaul, zone, appointmentType, dropDate, pickupDate);
    
    breakdown.containers.push({
      index: index + 1,
      containerNumber: container.container_number || `Container ${index + 1}`,
      size: container.container_size,
      weight: container.weight_lbs,
      ...charges
    });

    // Count overweight containers
    const weight = parseInt(container.weight_lbs) || 0;
    if (weight >= OVERWEIGHT_THRESHOLD) {
      breakdown.overweight.count += 1;
    }

    // Count hazmat containers
    if (container.hazmat) {
      breakdown.hazmat.count += 1;
    }
  });

  // Calculate overweight total
  breakdown.overweight.amount = breakdown.overweight.count * breakdown.overweight.perContainer;

  // Calculate hazmat total
  breakdown.hazmat.amount = breakdown.hazmat.count * breakdown.hazmat.perContainer;

  // Calculate drop charge (applies to all containers if conditions met)
  if (shouldApplyDropCharge) {
    breakdown.dropCharge.count = numContainers;
    breakdown.dropCharge.amount = numContainers * breakdown.dropCharge.perContainer;
  }

  // Calculate subtotal accessorials (mandatory + auto-applied)
  breakdown.subtotalAccessorials = 
    breakdown.chassisMinimum.amount +
    breakdown.chassisSplit.amount +
    breakdown.overweight.amount +
    breakdown.hazmat.amount +
    breakdown.dropCharge.amount;

  // Calculate total
  breakdown.total = breakdown.lineHaul.amount + breakdown.subtotalAccessorials;
  breakdown.grossTotal = breakdown.total;

  return breakdown;
};
